/**
 * Shared family loader (also defined in rk-style-picker.js, whichever loads first wins).
 * Fetches every product of a family once per page, e.g. all Sambas, with their
 * in-stock sizes, from /collections/all/<family-tag>?section_id=rk-size-alternatives.
 */
window.rkLoadFamily = window.rkLoadFamily || (function () {
  const cache = {};

  return function (family) {
    if (!family) return Promise.resolve([]);
    if (cache[family]) return cache[family];

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = root + 'collections/all/' + encodeURIComponent(family) + '?section_id=rk-size-alternatives';

    cache[family] = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const products = [];

        doc.querySelectorAll('script[data-rk-alt]').forEach((script) => {
          try {
            products.push(JSON.parse(script.textContent));
          } catch (error) {
            // Skip a product with malformed data rather than breaking the list
          }
        });

        return products;
      })
      .catch(() => []);

    return cache[family];
  };
})();

/**
 * <rk-style-picker>
 *
 * Adds the other in-stock styles of the same family next to the current product
 * (which Liquid already rendered first, with a black border). Sold-out styles are
 * skipped. Always 6 across: up to 12 styles wrap onto a second row; more than 12
 * become two rows that scroll sideways (filled left to right).
 *
 * The theme redraws the size picker block on every size change, which re-creates
 * this element; the family data is cached, so it re-renders instantly.
 */
function rkStyleName(title) {
  const match = (title || '').match(/["“”]([^"“”]+)["“”]/);
  return match ? match[1].trim() : title;
}

class RkStylePicker extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-rk-style-list]');
    this.nameEl = this.querySelector('[data-rk-style-name]');
    if (!this.list) return;

    this.list.addEventListener('mouseover', (event) => {
      const item = event.target.closest('.rk-style');
      if (item && this.nameEl) this.nameEl.textContent = item.dataset.name || '';
    });

    this.list.addEventListener('mouseleave', () => {
      if (this.nameEl) this.nameEl.textContent = this.nameEl.dataset.default || '';
    });

    window.rkLoadFamily(this.dataset.family).then((products) => this.render(products));
  }

  render(products) {
    const currentHandle = this.dataset.handle;
    const isKids = this.dataset.kids === 'true';

    const others = products.filter((p) =>
      p.handle !== currentHandle &&
      Boolean(p.kids) === isKids &&
      p.sizes && Object.keys(p.sizes).length > 0
    );

    // Keep only the current product (drawn by Liquid), then add the others
    this.list.querySelectorAll('.rk-style:not(.is-current)').forEach((el) => el.remove());

    if (others.length === 0) {
      this.hidden = true;
      return;
    }

    others.forEach((p) => {
      const link = document.createElement('a');
      link.className = 'rk-style';
      link.href = p.url;
      link.title = p.title;
      link.dataset.name = rkStyleName(p.title);

      if (p.image) {
        const img = document.createElement('img');
        img.src = p.image;
        img.alt = p.title;
        img.loading = 'lazy';
        img.width = 72;
        img.height = 72;
        link.appendChild(img);
      }

      this.list.appendChild(link);
    });

    // 6 columns; with more than 12 styles, widen to half the count so there are
    // exactly two rows (first half on top) that scroll sideways
    const count = this.list.querySelectorAll('.rk-style').length;
    this.list.style.setProperty('--rk-cols', String(Math.max(6, Math.ceil(count / 2))));

    this.hidden = false;
  }
}

if (!customElements.get('rk-style-picker')) {
  customElements.define('rk-style-picker', RkStylePicker);
}